import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/cn';
import { useIslandState, useIslandActions } from './IslandProvider';
import { computeIslandSize, istVollbild } from './island-size';
import { usePrefersReducedMotion, useViewport, useSichtbaresViewport, useFokusFalle } from './hooks';
import { HOVER_OPEN_MS, HOVER_CLOSE_MS, PAGE_META } from './constants';
import { GLAS, FEDER, EINBLENDEN } from './pille-stil';
import { LiveContent } from './states/LiveContent';
import { IdleContent } from './states/IdleContent';
import { TimerContent } from './states/TimerContent';
import { useTicketTimer } from '../../contexts/TicketTimerContext';
import { useNavigate } from 'react-router-dom';
import { ToastContent } from './states/ToastContent';
import { AnrufContent } from './states/AnrufContent';
import { useAnrufKontext } from './useAnrufKontext';
import { Deck } from './deck/Deck';
import { FormContent } from './states/FormContent';
import { GlasTooltipSchicht } from '../ui/GlasTooltip';
import { useNotification } from '../../contexts/NotificationContext';
import { Figgo, type FiggoZustand } from './Figgo';

/**
 * Die schwebende Pille (Handoff Pille): schwarzes Glas in Hell und Dunkel, Größe/Radius als
 * Feder (framer-motion), Hover öffnet, Klick heftet an, Esc schließt, ⌘K öffnet die Suche.
 * Inhalte je Zustand in ./states/* und ./deck/*.
 */
export function DynamicIsland({ mobileNavOpen = false }: { mobileNavOpen?: boolean }) {
  const state = useIslandState();
  const actions = useIslandActions();
  const { mode } = state;
  const reducedMotion = usePrefersReducedMotion();
  const viewport = useViewport();
  const sichtbar = useSichtbaresViewport();
  // Geöffnete Pille auf Handy-Breite ODER -Höhe = Vollbild (Position/Rundung
  // s. unten). Die Vollbild-ENTSCHEIDUNG hängt an `viewport` (Layout-Viewport,
  // window-Maße), nicht am sichtbaren: sonst würde die eingeblendete Tastatur
  // die sichtbare Höhe schrumpfen und mitten im Tippen von Panel auf Vollbild
  // umschalten. Die sichtbare Höhe (`sichtbar.hoehe`) DECKELT weiter unten nur
  // noch die Größe, damit die Fläche an der Tastaturkante endet.
  const istVoll = istVollbild(mode, viewport.width, viewport.height);
  const { unreadCount } = useNotification();
  const [hovered, setHovered] = useState(false);
  // 'pinned' = the user interacted with the open island, so hover-out won't close it
  const [pinned, setPinned] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOpen = mode === 'deck' || mode === 'form';
  // Laufender Ticket-Timer ersetzt in der ruhenden Pille Uhrzeit und Bereich (Handoff Tickets §7)
  const ticketTimer = useTicketTimer();
  const navigate = useNavigate();
  const mitTimer = mode === 'idle' && !!ticketTimer.timer;
  const peeking = mode === 'idle' && hovered && !reducedMotion && !mitTimer;

  // Einmal hier geladen (nicht in AnrufContent), damit die Höhe unten denselben
  // Stand kennt wie die Karte selbst — zwei Aufrufe des Hooks würden doppelt
  // laden und könnten kurzzeitig auseinanderlaufen.
  const anrufKontext = useAnrufKontext(state.anruf);

  const geschaetzt = computeIslandSize({
    mode,
    deckPage: state.deckPage,
    peeking,
    controlsContentHeight: state.controlsContentHeight,
    formWidth: state.formWidth,
    formContentHeight: state.formContentHeight,
    anrufHatAuswahl: !!state.anruf?.auswahl?.length,
    anrufKontext,
    anrufUnbekannt: !!state.anruf?.unbekannt,
    idleMitText: state.pageLabel !== null || unreadCount > 0,
    idleTimer: mitTimer,
    viewport: { width: viewport.width, height: Math.min(viewport.height, sichtbar.hoehe) },
    layoutHoehe: viewport.height,
  });
  // Anrufkarte: echte Inhaltshöhe messen (die Schätzung oben ist nur der Startwert)
  const [anrufHoehe, setAnrufHoehe] = useState<number | null>(null);
  const anrufMessung = useRef<ResizeObserver | null>(null);
  const anrufRef = (el: HTMLDivElement | null) => {
    anrufMessung.current?.disconnect();
    if (!el) { setAnrufHoehe(null); return; }
    anrufMessung.current = new ResizeObserver(() => setAnrufHoehe(Math.ceil(el.offsetHeight)));
    anrufMessung.current.observe(el);
  };
  const grund = mode === 'anruf' && anrufHoehe
    ? { ...geschaetzt, h: Math.min(anrufHoehe, Math.min(viewport.height, sichtbar.hoehe) - 96) }
    : geschaetzt;
  // Figgo: in Ruhe/Aktivität wird die Pille nur um seinen Platz (+44 px) breiter (Handoff §9)
  const [figgo, setFiggo] = useState<FiggoZustand>({ da: false, spricht: false });
  const figgoMeldet = useCallback((z: FiggoZustand) => {
    setFiggo(alt => (alt.da === z.da && alt.spricht === z.spricht ? alt : z));
  }, []);
  const figgoRuhig = figgo.da && (mode === 'idle' || mode === 'live');
  const figgoAusblenden = figgoRuhig && figgo.spricht;
  // Mobil (< 768 px) nicht breiter, sonst überdeckt die Pille Menü/Profilbild der Kopfzeile
  const figgoExtra = figgoRuhig && viewport.width >= 768 ? 44 : 0;
  const size = figgoExtra ? { ...grund, w: Math.min(grund.w + figgoExtra, viewport.width - 16) } : grund;

  // reset 'pinned' on resting states; form/search are always pinned tasks
  useEffect(() => {
    if (mode === 'idle' || mode === 'live' || mode === 'toast' || mode === 'anruf') setPinned(false);
    if (mode === 'form') setPinned(true);
  }, [mode]);

  function clearTimers() {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }
  function activate() {
    if (state.pageControls) actions.openPage();
    else actions.expand();
  }
  function handleEnter() {
    setHovered(true);
    clearTimers();
    // Mit laufendem Ticket-Timer nicht per Hover aufklappen (sonst springt das Menü über Ticket-/Stopp-Knopf);
    // ein Klick bzw. Tipp auf die Pille öffnet sie weiterhin.
    if ((mode === 'idle' && !mitTimer) || mode === 'live') {
      openTimer.current = setTimeout(activate, HOVER_OPEN_MS);
    }
  }
  function handleLeave() {
    setHovered(false);
    clearTimers();
    if (!pinned && mode === 'deck') {
      closeTimer.current = setTimeout(actions.collapse, HOVER_CLOSE_MS);
    }
  }
  useEffect(() => () => clearTimers(), []);

  // ESC schließt das Deck. Das Formular behandelt Escape selbst (FormContent), damit eine
  // aufgeklappte Auswahlliste die Taste zuerst für sich verbrauchen kann.
  useEffect(() => {
    if (mode !== 'deck') return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') actions.collapse();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, actions]);

  // global ⌘K / Ctrl+K → open search
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        actions.openSearch();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions]);

  const pillenRef = useRef<HTMLDivElement>(null);
  useFokusFalle(pillenRef, isOpen, `${mode}:${state.deckPage}`);

  const renderIdle = () => ticketTimer.timer ? (
    <TimerContent
      beginn={ticketTimer.timer.zeit.beginn}
      jetzt={ticketTimer.jetzt}
      ticketNummer={ticketTimer.timer.ticketNummer}
      schmal={viewport.width < 768}
      onOeffnen={() => navigate(`/admin/tickets/${ticketTimer.timer!.zeit.ticket_id}?einsatz=zeiten`)}
      onStoppen={ticketTimer.stoppen}
    />
  ) : (
    <IdleContent
      peeking={peeking}
      unread={unreadCount}
      pageLabel={state.pageLabel}
    />
  );

  // Vollbild liegt über der mobilen Leiste (z 90) und ihrem Mehr-Sheet (z 120).
  // Meldung und Anruf liegen kurz über der mobilen Kopfzeile (Menü-Knopf z 60), damit
  // Icon/Anrufer nicht verdeckt werden; Ruhe/Aktivität bleiben darunter (Menü bedienbar).
  const ebene = istVoll ? 130 : mode === 'toast' || mode === 'anruf' ? 70 : 50;
  const dialogLabel = mode === 'form' ? (state.formTitle ?? 'Formular') : PAGE_META[state.deckPage].label;

  return (
    <>
      {isOpen && !istVoll && (
        <div aria-hidden className="fixed inset-0" onClick={actions.collapse}
          style={{ zIndex: ebene - 1, background: GLAS.backdrop, backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }} />
      )}

      {/* fixed: sitzt unter der Notch (safe-area). Handy + geöffnet = Vollbild oben links.
          Ausgeblendet, solange die mobile Navigation (Drawer) offen ist. */}
      <div
        className={cn(
          'pointer-events-none fixed',
          istVoll
            ? 'left-0 right-0 top-0'
            : 'left-1/2 top-[calc(0.5rem_+_env(safe-area-inset-top))] -translate-x-1/2',
          mobileNavOpen && 'hidden',
        )}
        // Vollbild deckt sich mit dem SICHTBAREN Viewport (iOS verschiebt ihn bei Tastatur).
        style={{ zIndex: ebene, ...(istVoll && sichtbar.offsetTop ? { transform: `translateY(${Math.round(sichtbar.offsetTop)}px)` } : {}) }}
      >
        <motion.div
          ref={pillenRef}
          data-pille=""
          role={isOpen ? 'dialog' : 'button'}
          aria-modal={isOpen ? true : undefined}
          aria-label={isOpen ? dialogLabel : state.pageLabel ? `${state.pageLabel} steuern – Pille öffnen` : 'Pille öffnen'}
          aria-expanded={isOpen ? undefined : false}
          tabIndex={isOpen ? -1 : 0}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onPointerDownCapture={() => { if (isOpen) setPinned(true); }}
          onFocusCapture={() => { if (isOpen) setPinned(true); }}
          onClick={() => {
            clearTimers();
            if (mode === 'idle' || mode === 'live' || mode === 'toast' || mode === 'anruf') activate();
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !isOpen && e.target === e.currentTarget) {
              e.preventDefault();
              activate();
            }
          }}
          initial={false}
          animate={{ width: size.w, height: size.h, borderRadius: size.r }}
          transition={reducedMotion ? { duration: 0 } : FEDER}
          className={cn(
            'pointer-events-auto relative overflow-clip text-white outline-none',
            !isOpen && 'cursor-pointer',
            'focus-visible:ring-2 focus-visible:ring-[#e2c08d]/60',
            // Vollbild: randlos, Inhalt unter der Notch und über der Home-Leiste.
            istVoll && 'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]',
          )}
          style={{
            background: GLAS.hintergrund,
            backdropFilter: GLAS.filter,
            WebkitBackdropFilter: GLAS.filter,
            boxShadow: istVoll ? 'none' : isOpen ? GLAS.schattenOffen : GLAS.schattenZu,
            fontFamily: "'Manrope', -apple-system, 'Helvetica Neue', sans-serif",
            WebkitFontSmoothing: 'antialiased',
          }}
        >
          {/* oberer Glanz */}
          <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: GLAS.glanz, borderRadius: 'inherit' }} />

          {/* Inhalt – je Zustand neu eingeblendet. Gescrollt wird eine Ebene tiefer (Formular, Deck-Seiten). */}
          <motion.div
            key={mode}
            className="relative h-full"
            initial={reducedMotion ? false : EINBLENDEN.initial}
            animate={EINBLENDEN.animate}
            transition={EINBLENDEN.transition}
          >
            {(mode === 'idle' || mode === 'live') && (
              <div className="h-full" style={{
                paddingLeft: figgoRuhig ? 36 : 0,
                opacity: figgoAusblenden ? 0 : 1,
                filter: figgoAusblenden ? 'blur(4px)' : 'none',
                transition: 'opacity .3s, filter .3s, padding .3s',
              }}>
                {mode === 'idle' ? renderIdle() : <LiveContent />}
              </div>
            )}
            {mode === 'toast' && <ToastContent />}
            {mode === 'anruf' && <div ref={anrufRef}><AnrufContent kontext={anrufKontext} /></div>}
            {mode === 'deck' && <Deck reducedMotion={reducedMotion} />}
            {mode === 'form' && <FormContent mobil={istVoll} />}
          </motion.div>
          <Figgo pillRef={pillenRef} ziel={size} hovered={hovered} onZustand={figgoMeldet} />
          {/* Glas-Tooltips für alles in der Pille (Formulare, Deck, Timer) – Seiten-Schichten lassen die Pille aus. */}
          <GlasTooltipSchicht nurPille />
        </motion.div>
      </div>
    </>
  );
}
